import { html, nothing } from "lit";
import type { SessionContextBreakdown } from "../controllers/chat-context-breakdown.ts";
import {
  formatBreakdownTokens,
  type ContextBreakdownCategoryId,
} from "../controllers/chat-context-breakdown.ts";
import { icons } from "../icons.ts";

const SEGMENT_CLASS: Record<ContextBreakdownCategoryId, string> = {
  systemPrompt: "system",
  tools: "tools",
  rules: "rules",
  skills: "skills",
  mcp: "mcp",
  subagents: "subagents",
  summarizedConversation: "summarized",
  conversation: "conversation",
};

function pct(part: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return (part / total) * 100;
}

export type ChatContextBreakdownPanelProps = {
  open: boolean;
  loading: boolean;
  error: string | null;
  breakdown: SessionContextBreakdown | null;
  onClose: () => void;
  onRefresh?: () => void;
};

export function renderChatContextBreakdownPanel(props: ChatContextBreakdownPanelProps) {
  if (!props.open) {
    return nothing;
  }

  const breakdown = props.breakdown;
  const barTotal = breakdown
    ? Math.max(
        breakdown.usedTokens,
        breakdown.categories.reduce((s, c) => s + c.tokens, 0),
        1,
      )
    : 1;

  return html`
    <div
      class="chat-context-panel-backdrop"
      role="presentation"
      @click=${(e: MouseEvent) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        class="chat-context-panel"
        role="dialog"
        aria-labelledby="chat-context-panel-title"
        aria-modal="true"
        @click=${(e: Event) => e.stopPropagation()}
      >
        <header class="chat-context-panel__header">
          <h2 id="chat-context-panel-title" class="chat-context-panel__title">Context usage</h2>
          <div class="chat-context-panel__header-actions">
            ${
              props.onRefresh
                ? html`
                    <button
                      type="button"
                      class="btn btn--sm"
                      ?disabled=${props.loading}
                      @click=${props.onRefresh}
                    >
                      Refresh
                    </button>
                  `
                : nothing
            }
            <button
              type="button"
              class="btn btn--sm btn--icon"
              aria-label="Close context panel"
              @click=${props.onClose}
            >
              ${icons.x}
            </button>
          </div>
        </header>

        ${
          props.loading
            ? html`
                <p class="chat-context-panel__status muted">Loading breakdown…</p>
              `
            : props.error
              ? html`<p class="chat-context-panel__status chat-context-panel__status--error">${props.error}</p>`
              : !breakdown
                ? html`
                    <p class="chat-context-panel__status muted">No context data for this session yet.</p>
                  `
                : renderBreakdownBody(breakdown, barTotal)
        }
      </section>
    </div>
  `;
}

function renderBreakdownBody(breakdown: SessionContextBreakdown, barTotal: number) {
  const fillLabel =
    breakdown.percent != null
      ? `${breakdown.percent}% full`
      : breakdown.limitTokens
        ? "—"
        : "No limit set";

  return html`
    <div class="chat-context-panel__summary">
      <div class="chat-context-panel__fill">
        <span class="chat-context-panel__fill-value">${fillLabel}</span>
        <span class="chat-context-panel__fill-detail mono">
          ${formatBreakdownTokens(breakdown.usedTokens)}
          ${
            breakdown.limitTokens
              ? html`/ ${formatBreakdownTokens(breakdown.limitTokens)} tokens`
              : html`
                  tokens used
                `
          }
        </span>
      </div>
      ${
        breakdown.model
          ? html`<p class="chat-context-panel__model muted mono">${breakdown.model}</p>`
          : nothing
      }
      <p class="chat-context-panel__source muted">
        Source: ${breakdown.reportSource === "run" ? "last agent run" : "estimate"}
      </p>
    </div>

    <div class="chat-context-panel__bar context-stacked-bar" aria-hidden="true">
      ${breakdown.categories
        .filter((c) => c.tokens > 0)
        .map(
          (c) => html`
            <div
              class="context-segment ${SEGMENT_CLASS[c.id]}"
              style="width: ${pct(c.tokens, barTotal).toFixed(1)}%"
              title="${c.label}: ~${formatBreakdownTokens(c.tokens)}"
            ></div>
          `,
        )}
    </div>

    <ul class="chat-context-panel__legend context-legend">
      ${breakdown.categories.map(
        (c) => html`
          <li class="context-legend__item">
            <span class="context-legend__swatch context-segment ${SEGMENT_CLASS[c.id]}"></span>
            <span class="context-legend__label">${c.label}</span>
            <span class="context-legend__value mono">~${formatBreakdownTokens(c.tokens)}</span>
          </li>
        `,
      )}
    </ul>

    ${
      breakdown.subagentSessions.length > 0
        ? html`
            <details class="chat-context-panel__details">
              <summary>Subagent sessions (${breakdown.subagentSessions.length})</summary>
              <ul class="chat-context-panel__subagents">
                ${breakdown.subagentSessions.map(
                  (s) => html`
                    <li>
                      <span class="mono">${s.label ?? s.sessionKey}</span>
                      <span class="muted">~${formatBreakdownTokens(s.tokens)}</span>
                    </li>
                  `,
                )}
              </ul>
            </details>
          `
        : nothing
    }
  `;
}
